import { AuthUser, LoginCredentials, AuthSession } from '@/types/auth';
import { ApiResponse, StorageKeys, LoginApiResponse } from '@/types/api';
import { StorageService } from './storageService';
import { ApiService } from './api';

export class AuthService {
  private static instance: AuthService;
  private storageService: StorageService;
  private apiService: ApiService;
  private refreshTimer: NodeJS.Timeout | null = null;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private constructor() {
    this.storageService = StorageService.getInstance();
    this.apiService = ApiService.getInstance();
    
    // Initialize auth state from stored session
    this.initializeAuthState();
  }

  // Initialize authentication state on app start
  private async initializeAuthState(): Promise<void> {
    const session = this.storageService.getItem<AuthSession>(StorageKeys.AUTH_SESSION);
    
    if (session && session.token) {
      this.apiService.setAuthToken(session.token);
      
      // Validate the stored token with the server
      const validation = await this.apiService.validateToken();
      if (!validation.success) {
        // Token is invalid, clear session
        this.logout();
      } else {
        // Set up refresh timer
        this.setupTokenRefresh(session);
      }
    }
  }

  // Login with credentials
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthUser>> {
    try {
      const response = await this.apiService.login(credentials.username, credentials.password);
      
      if (!response.success) {
        return {
          success: false,
          error: response.error || 'Login failed',
          timestamp: Date.now()
        };
      }

      const loginData = response.data as LoginApiResponse;
      
      // Calculate expiration time (use the expiresIn from backend)
      const now = Date.now();
      const expiresAt = now + (loginData.expiresIn * 1000); // Convert seconds to milliseconds
      
      const user: AuthUser = {
        username: loginData.user.username,
        isAuthenticated: true,
        loginTime: now,
        expiresAt,
        lastLogin: loginData.user.lastLogin ? new Date(loginData.user.lastLogin) : undefined
      };

      const session: AuthSession = {
        token: loginData.token,
        refreshToken: loginData.refreshToken,
        user,
        expiresAt
      };

      // Store session and set API token
      this.storageService.setItem(StorageKeys.AUTH_SESSION, session);
      this.apiService.setAuthToken(loginData.token);
      
      // Setup token refresh
      this.setupTokenRefresh(session);

      return {
        success: true,
        data: user,
        timestamp: Date.now()
      };
      
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'Erro interno do servidor',
        timestamp: Date.now()
      };
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      // Call backend logout endpoint
      await this.apiService.logout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear local state regardless of API call result
      this.clearAuthState();
    }
  }

  // Clear authentication state
  private clearAuthState(): void {
    this.storageService.removeItem(StorageKeys.AUTH_SESSION);
    this.apiService.setAuthToken(null);
    
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Get current user
  getCurrentUser(): AuthUser | null {
    const session = this.storageService.getItem<AuthSession>(StorageKeys.AUTH_SESSION);
    
    if (!session || !this.isSessionValid(session)) {
      this.clearAuthState();
      return null;
    }

    return session.user;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    return user !== null && user.isAuthenticated;
  }

  // Validate session
  private isSessionValid(session: AuthSession): boolean {
    const now = Date.now();
    return session.expiresAt > now;
  }

  // Get session info
  getSessionInfo(): { expiresIn: number; isValid: boolean } | null {
    const session = this.storageService.getItem<AuthSession>(StorageKeys.AUTH_SESSION);
    
    if (!session) return null;

    const now = Date.now();
    const expiresIn = session.expiresAt - now;
    const isValid = expiresIn > 0;

    return { expiresIn, isValid };
  }

  // Setup automatic token refresh
  private setupTokenRefresh(session: AuthSession): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!session.refreshToken) return;

    // Refresh token when 75% of the time has passed
    const now = Date.now();
    const timeUntilRefresh = (session.expiresAt - now) * 0.75;
    
    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(async () => {
        await this.refreshAuthToken(session.refreshToken!);
      }, timeUntilRefresh);
    }
  }

  // Refresh authentication token
  private async refreshAuthToken(refreshToken: string): Promise<boolean> {
    try {
      const response = await this.apiService.refreshToken(refreshToken);
      
      if (response.success && response.data) {
        const loginData = response.data as LoginApiResponse;
        
        // Update session with new tokens
        const now = Date.now();
        const expiresAt = now + (loginData.expiresIn * 1000);
        
        const session: AuthSession = {
          token: loginData.token,
          refreshToken: loginData.refreshToken,
          user: {
            ...this.getCurrentUser()!,
            expiresAt
          },
          expiresAt
        };

        // Store updated session and set new API token
        this.storageService.setItem(StorageKeys.AUTH_SESSION, session);
        this.apiService.setAuthToken(loginData.token);
        
        // Setup next refresh
        this.setupTokenRefresh(session);
        
        return true;
      } else {
        // Refresh failed, logout user
        this.clearAuthState();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearAuthState();
      return false;
    }
  }

  // Manual session extension (for user activity)
  async extendSession(): Promise<boolean> {
    const session = this.storageService.getItem<AuthSession>(StorageKeys.AUTH_SESSION);
    
    if (!session || !session.refreshToken) {
      return false;
    }

    return this.refreshAuthToken(session.refreshToken);
  }

  // Check if token needs refresh soon (within 5 minutes)
  needsRefresh(): boolean {
    const sessionInfo = this.getSessionInfo();
    if (!sessionInfo || !sessionInfo.isValid) return false;
    
    // Return true if token expires within 5 minutes
    return sessionInfo.expiresIn < 5 * 60 * 1000;
  }
}